<script lang="ts">
  import type { Message } from '../../shared/types';
  import { isHorizontalDrag, isReplySwipe, replyDragOffset, swipeProgress } from '../gestures';
  import { formatClock } from '../ui';
  import Attachments from './Attachments.svelte';
  import Avatar from './Avatar.svelte';
  import Icon from './Icon.svelte';

  interface Props {
    message: Message;
    own: boolean;
    grouped: boolean;
    onReply: (message: Message) => void;
  }

  let { message, own, grouped, onReply }: Props = $props();

  let drag = $state<{ offset: number; progress: number } | null>(null);
  let start: { x: number; y: number } | null = null;
  let horizontal: boolean | null = null;

  function onTouchStart(event: TouchEvent) {
    const touch = event.touches[0];
    start = { x: touch.clientX, y: touch.clientY };
    horizontal = null;
  }

  function onTouchMove(event: TouchEvent) {
    const origin = start;
    if (!origin) return;
    const touch = event.touches[0];
    const dx = touch.clientX - origin.x;
    const dy = touch.clientY - origin.y;
    if (horizontal === null) {
      if (!isHorizontalDrag(dx, dy)) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) horizontal = false;
        return;
      }
      horizontal = true;
    }
    if (!horizontal || dx >= 0) {
      if (drag) drag = null;
      return;
    }
    drag = { offset: replyDragOffset(dx), progress: swipeProgress(dx) };
  }

  function onTouchEnd(event: TouchEvent) {
    const origin = start;
    start = null;
    horizontal = null;
    drag = null;
    if (!origin) return;
    const touch = event.changedTouches[0];
    if (isReplySwipe(touch.clientX - origin.x, touch.clientY - origin.y)) onReply(message);
  }

  function onTouchCancel() {
    start = null;
    horizontal = null;
    drag = null;
  }
</script>

<div
  class={`msg-row ${own ? 'self' : 'other'} ${grouped ? 'grouped' : ''} ${drag ? 'swiping' : ''}`}
  ontouchstart={onTouchStart}
  ontouchmove={onTouchMove}
  ontouchend={onTouchEnd}
  ontouchcancel={onTouchCancel}
>
  {#if !own}
    <div class="msg-avatar">
      {#if !grouped}
        <Avatar name={message.author.name} url={message.author.avatar} size={32} />
      {/if}
    </div>
  {/if}
  <div
    class={`bubble ${message.status === 'failed' ? 'failed' : ''}`}
    style:transform={drag ? `translateX(${drag.offset}px)` : undefined}
  >
    {#if !own && !grouped}
      <div class="bubble-author">{message.author.name || 'Peer'}</div>
    {/if}
    {#if message.replyPreview}
      <div class="reply-quote"><span>{message.replyPreview}</span></div>
    {/if}
    {#if message.attachments?.length}
      <Attachments attachments={message.attachments} />
    {/if}
    {#if message.text}
      <div class="bubble-text">
        {message.text}
        {#if message.streaming}<span class="caret"></span>{/if}
      </div>
    {/if}
    <div class="bubble-meta">
      <span>{formatClock(message.ts)}</span>
      {#if message.editedAt}<span class="edited">edited</span>{/if}
      {#if own && message.status === 'sending'}<span class="pending"></span>{/if}
      {#if own && message.status === 'sent'}<Icon name="check" size={12} />{/if}
      {#if message.status === 'failed'}<span class="failed-tag">failed</span>{/if}
    </div>
    {#if message.error}
      <div class="bubble-error">{message.error}</div>
    {/if}
  </div>
  <button class="reply-btn" onclick={() => onReply(message)} title="Reply" aria-label="Reply">
    <Icon name="reply" size={14} />
  </button>
  <span
    class={`swipe-reply ${drag && drag.progress >= 1 ? 'armed' : ''}`}
    style:opacity={drag ? drag.progress : 0}
    aria-hidden="true"
  >
    <Icon name="reply" size={14} />
  </span>
</div>
